import { useCallback, useRef } from "react";

import isEqual from "lodash/isEqual";

/**
 * Remembers an amendment write that already saved, so a Sign & Lock retry after a later step
 * failed reuses its result instead of writing the same values again. A write that throws is not
 * remembered, and new values always write.
 *
 * @returns A runner that calls `write` unless these exact values already saved, and resolves its result.
 */
export function useAmendmentWriteOnce<Values, Result>() {
  const savedWriteRef = useRef<{ values: Values; result: Result }>();

  return useCallback(async (values: Values, write: () => Promise<Result>): Promise<Result> => {
    const savedWrite = savedWriteRef.current;
    if (savedWrite && isEqual(savedWrite.values, values)) return savedWrite.result;

    const result = await write();
    savedWriteRef.current = { values, result };
    return result;
  }, []);
}
